import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Play, ExternalLink, Music2 } from 'lucide-react';

const MusicSection = ({ musicData }) => {
  if (!musicData || musicData.length === 0) return null;

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'spotify':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        );
      case 'apple':
      case 'apple music':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.997 6.124c0-.738-.065-1.47-.24-2.19-.317-1.31-1.062-2.31-2.18-3.043C21.003.517 20.373.285 19.7.164c-.517-.093-1.038-.135-1.564-.15-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026C4.786.07 4.043.15 3.34.428 2.004.958 1.04 1.88.475 3.208c-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.296-.81a5.008 5.008 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.043-1.85-.175-2.655-.76-.88-.64-1.233-1.536-1.123-2.64.11-1.13.695-1.937 1.774-2.287.718-.234 1.466-.28 2.22-.27.096.002.195.01.29.014v-3.992c0-.26-.01-.522.007-.782.01-.165.09-.237.254-.25.777-.063 1.554-.13 2.33-.197.687-.06 1.375-.118 2.062-.177.16-.014.314-.1.393.16.028.093.028.196.028.295-.002 1.37 0 2.742 0 4.113 0 .02-.006.04-.01.077-.26 0-.495-.003-.73-.007-.502-.008-1.003-.013-1.504-.028-.17-.005-.245-.09-.252-.26-.014-.33-.005-.66-.005-.992v-.645z"/>
          </svg>
        );
      case 'youtube':
      case 'youtube music':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        );
      default:
        return <ExternalLink className="w-5 h-5" />;
    }
  };

  return (
    <section id="music" className="py-24 px-6 relative overflow-hidden bg-black">
      {/* Background sacred geometry */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 left-1/4 w-96 h-96">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <circle cx="100" cy="100" r="80" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="60" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
          </svg>
        </div>
      </div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
            <Music2 className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-medium text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Music</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Cinzel, serif' }}>
            Sonic Journeys
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Immersive soundscapes blending ancient instruments with contemporary production
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {musicData.map((track, index) => (
            <Card 
              key={track.id} 
              className="bg-black border-2 border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-300 overflow-hidden group"
            >
              <CardContent className="p-0">
                <div className="relative">
                  {/* Album/Single Cover */}
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={track.image}
                      alt={track.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-transparent to-transparent opacity-60"></div>
                    
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40">
                      <Button 
                        className="bg-[#D4AF37] hover:bg-[#2DD4BF] text-black rounded-full w-16 h-16 p-0"
                      >
                        <Play className="w-8 h-8" fill="currentColor" />
                      </Button>
                    </div>
                    
                    {/* Type badge */}
                    <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-semibold text-[#D4AF37] border border-[#D4AF37]/30">
                      {track.type.toUpperCase()}
                    </div>
                  </div>
                  
                  {/* Track Info */}
                  <div className="p-6 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                          {track.title}
                        </h3>
                        <span className="text-sm text-gray-400">{track.releaseDate}</span>
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {track.description}
                      </p>
                    </div>
                    
                    {/* Platform Links */}
                    <div className="pt-4 border-t border-purple-500/20">
                      <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
                        Listen on
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {track.links.map((link, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors"
                            onClick={() => window.open(link.url, '_blank')}
                          >
                            <span className="mr-2">{getPlatformIcon(link.platform)}</span>
                            {link.platform}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* All Platforms Section */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 rounded-2xl bg-[#D4AF37]/5 border border-[#D4AF37]/30">
            <p className="text-lg mb-6 text-gray-300">
              Available on all major streaming platforms
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Spotify', 'Apple Music', 'YouTube Music', 'Tidal', 'Amazon Music', 'Deezer'].map((platform) => (
                <Button
                  key={platform}
                  variant="outline"
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                  onClick={() => alert(`Opening ${platform}...`)}
                >
                  {platform}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MusicSection;
